import { createReducer, on } from "@ngrx/store";
import { initialSensorState, initialSiteState, IStatistic, StatisticMap } from "./site.state";
import { SiteActions } from './site.actions'
import { onLoadableLoad, onLoadableSuccess, onLoadableError, createDefaultLoadable, arrayToILoadableWrapperArray, toLoadableWrapper } from '../../models/index';
import { objectListToMap, NEW_KEY_ID, excludeKey, replaceItem, removeItem } from "../common";
import { UserActions } from "../user/user.actions";
import { composeReducers } from "../common/helpers";
import { sortSensorsByStatistics } from './site.helpers';
import { ILoadableWrapper } from "../../models/index";
import { updateSensorsInSiteState } from "./helpers/sensor.helpers";


const _siteReducer = createReducer(
    initialSiteState,
    on(SiteActions.loadSites, (state, { flush })  => {
        return { ...onLoadableLoad(state), sites: flush ? {} : state.sites }
    }),
    on(SiteActions.loadSitesSuccess, (state, { sites }) => ({
        ...onLoadableSuccess(state), sites: objectListToMap(sites.map(e => {
            return {
                ...e,
                ...createDefaultLoadable()
            }
        }))
    })),
    on(SiteActions.loadSitesFailure, (state, { error }) => ({ ...onLoadableError(state, error) })),
    on(SiteActions.setCurrentSite, (state, { site }) => ({ ...state, selectedSite: site.id })),
    on(SiteActions.clearCurrentSite, (state) => ({ ...state, selectedSite: null })),
    on(SiteActions.loadSensors, (state, { site }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [site.id]: {
                ...onLoadableLoad({ ...(state.sensors[site.id] ?? initialSensorState) })
            }
        }
    })),
    on(SiteActions.loadSensorsSuccess, (state, { site, sensors }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [site.id]: {
                ...onLoadableSuccess({ ...(state.sensors[site.id]) }), items: arrayToILoadableWrapperArray(sensors)
            }
        }
    })),
    on(SiteActions.loadSensorsFailure, (state, { site, error }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [site.id]: {
                ...onLoadableError({ ...(state.sensors[site.id]) }, error)
            }
        }
    })),
    on(SiteActions.registerSite, (state, { site }) => ({
        ...state,
        sites: {
            ...state.sites,
            [NEW_KEY_ID]: {
                ...onLoadableLoad({ ...site, ...createDefaultLoadable()})
            }
        }
    })),
    on(SiteActions.registerSiteSuccess, (state, { site }) => ({
        ...state,
        sites: excludeKey({...state.sites, [site.id]: onLoadableSuccess({...state.sites[NEW_KEY_ID], ...site})}, NEW_KEY_ID)
    })),
    on(SiteActions.registerSiteFailure, (state, { error }) => ({
        ...state,
        sites: {
            ...state.sites,
            [NEW_KEY_ID]: {
                ...onLoadableError({ ...state.sites[NEW_KEY_ID], ...createDefaultLoadable()}, error)
            }
        }
    })),
    on(SiteActions.updateSite, (state, { site }) => ({
        ...state,
        sites: {
            ...state.sites,
            [site.id]: {
                ...onLoadableLoad({...state.sites[site.id],...site})
            }
        }
    })),
    on(SiteActions.updateSiteSuccess, (state, { site }) => ({
        ...state,
        sites: {...state.sites, [site.id]: onLoadableSuccess({...state.sites[site.id], ...site})}
    })),
    on(SiteActions.updateSiteFailure, (state, { site, error }) => ({
        ...state,
        sites: {
            ...state.sites,
            [site.id]: {
                ...onLoadableError({ ...state.sites[site.id]}, error)
            }
        }
    })),
    on(SiteActions.deleteSite, (state, { site }) => ({
        ...state,
        sites: {
            ...state.sites,
            [site.id]: {
                ...onLoadableLoad({...state.sites[site.id],...site})
            }
        }
    })),
    on(SiteActions.deleteSiteSuccess, (state, { site }) => ({
        ...state,
        sites: excludeKey(state.sites, site.id)
    })),
    on(SiteActions.deleteSiteFailure, (state, { site, error }) => ({
        ...state,
        sites: {
            ...state.sites,
            [site.id]: {
                ...onLoadableError({ ...state.sites[site.id]}, error)
            }
        }
    })),
    on(SiteActions.updateSensor, (state, { sensor }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                ...onLoadableLoad(state.sensors[sensor.site_id]),
                items: replaceItem(state.sensors[sensor.site_id].items, onLoadableLoad(toLoadableWrapper({...sensor})))
            }
        }
    })),
    on(SiteActions.updateSensorSuccess, (state, { sensor }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                ...onLoadableSuccess(state.sensors[sensor.site_id]),
                items: replaceItem(state.sensors[sensor.site_id].items, onLoadableSuccess(toLoadableWrapper({...sensor})))
            }
        }
    })),
    on(SiteActions.updateSensorError, (state, { sensor, error }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                ...onLoadableError(state.sensors[sensor.site_id], error),
                items: replaceItem(state.sensors[sensor.site_id].items, onLoadableError(state.sensors[sensor.site_id].items.filter(e => e.id === sensor.id)[0], error))
            }
        }
    })),
    on(SiteActions.createSensors, (state, { sensors }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensors[0].site_id]: onLoadableLoad(state.sensors[sensors[0].site_id])
        }
    })),
    on(SiteActions.createSensorsSuccess, (state, { sensors }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensors[0].site_id]: onLoadableSuccess(state.sensors[sensors[0].site_id])
        }
    })),
    on(SiteActions.createSensorsError, (state, { siteIDs, error }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [siteIDs[0]]: onLoadableError(state.sensors[siteIDs[0]], error)
        }
    })),
    on(SiteActions.deleteSensor, (state, { sensor }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                items: replaceItem(state.sensors[sensor.site_id].items, onLoadableLoad(toLoadableWrapper({...sensor})))
            }
        }
    })),
    on(SiteActions.deleteSensorSuccess, (state, { sensor }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                items: removeItem(state.sensors[sensor.site_id].items, sensor.id)
            }
        }
    })),
    on(SiteActions.deleteSensorError, (state, { sensor, error }) => ({
        ...state,
        sensors: {
            ...state.sensors,
            [sensor.site_id]: {
                ...state.sensors[sensor.site_id],
                items: replaceItem(state.sensors[sensor.site_id].items, onLoadableError(state.sensors[sensor.site_id].items.filter(e => e.id === sensor.id)[0], error))
            }
        }
    })),
    on(SiteActions.enableSensors, SiteActions.disableSensors, (state, { sensors }) =>
        updateSensorsInSiteState(state, sensors, onLoadableLoad)
    ),
    on(SiteActions.enableSensorsSuccess, (state, { sensors }) =>
        updateSensorsInSiteState(state, sensors, (sensor) => ({
            ...onLoadableSuccess(sensor),
            enabled: true,
        }))
    ),
    on(SiteActions.disableSensorsSuccess, (state, { sensors }) =>
        updateSensorsInSiteState(state, sensors, (sensor) => ({
            ...onLoadableSuccess(sensor),
            enabled: false,
        }))
    ),
    on(SiteActions.enableSensorsFailure, SiteActions.disableSensorsFailure, (state, { sensors, error }) =>
        updateSensorsInSiteState(state, sensors, (sensor) => ({
            ...onLoadableError(sensor, error),
        }))
    ),
    on(SiteActions.setShowRulesOnPatrol, (state, { enabled }) => ({
        ...state,
        showRulesOnPatrol: enabled
    })),
)

const _statisticReducer = createReducer(
    initialSiteState,
    on(SiteActions.loadSensorStatistics, (state) => ({
        ...state,
        sensorStatistics: {
            ...onLoadableLoad(state.sensorStatistics),
        }
    })),
    on(SiteActions.loadSensorStatisticsSuccess, (state, { sensors, statistics }) => {
      const updatedSensorStatistics: ILoadableWrapper<Record<string, IStatistic>> = {
          ...onLoadableSuccess(state.sensorStatistics),
          ...sensors.reduce((acc, curr, index) => {
              acc[curr.id] = statistics[index];
              return acc;
          }, {} as StatisticMap)
      };
      const siteID: string = sensors[0].site_id;
      return {
          ...state,
          sensorStatistics: updatedSensorStatistics,
          sensors: {
              ...state.sensors,
              [siteID]: {
                  ...state.sensors[siteID],
                  items: sortSensorsByStatistics(
                      state.sensors[siteID].items,
                      updatedSensorStatistics
                  )
              }
          }
      };
    }),
    on(SiteActions.loadSensorStatisticsFailure, (state, { error }) => ({
        ...state,
        sensorStatistics: {
            ...onLoadableError(state.sensorStatistics, error)
        }
    })),
    on(SiteActions.loadSiteStatistics, (state) => ({
        ...state,
        siteStatistics: {
            ...onLoadableLoad(state.siteStatistics),
        }
    })),
    on(SiteActions.loadSiteStatisticsSuccess, (state, { site, statistics }) => ({
        ...state,
        siteStatistics: {
            ...onLoadableSuccess(state.sensorStatistics),
            ...statistics.reduce((acc, curr, index) => {
                acc[site.id] = statistics[index];
                return acc
            }, {} as StatisticMap)
        }
    })),
    on(SiteActions.loadSiteStatisticsFailure, (state, { error }) => ({
        ...state,
        siteStatistics: {
            ...onLoadableError(state.siteStatistics, error)
        }
    })),
    on(UserActions.logout, () => ({ ...initialSiteState })),
)

export const siteReducer = composeReducers(initialSiteState, _siteReducer, _statisticReducer)
